import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { ChatConversation, ChatMessage } from '@/types/chat.types'
import { supabase } from '@/integrations/supabase/client'
import { Message } from './Message'
import { useMessageReadTracking } from '@/hooks/useMessageReadTracking'
import { useProgramAgents } from '@/hooks/useProgramAgents'
import { usePlanInfo } from '@/hooks/usePlanInfo'
import { AgentAvatar } from './AgentAvatar'
import { AssignmentDropdown } from './AssignmentDropdown'
import { VisitorContextSidebar } from './visitor-context'
import {
	Loader2,
	Send,
	Paperclip,
	MoreHorizontal,
	ArrowLeft,
	Trash2,
	XCircle,
	X,
	CheckCheck,
	UserPlus,
	ChevronDown,
	ChevronUp,
	Info,
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/context/AuthContext'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'

interface ChatWindowProps {
	conversationId: string
	onBack?: () => void
	onConversationDeleted?: () => void
	visitorName?: string
	visitorEmail?: string
}

// Message grouping logic
const shouldShowSenderInfo = (messages: ChatMessage[], index: number): boolean => {
	if (index === 0) return true

	const current = messages[index]
	const previous = messages[index - 1]

	// Different sender type
	if (current.sender_type !== previous.sender_type) return true

	// Time gap > 5 minutes
	const timeDiff = new Date(current.created_at).getTime() - new Date(previous.created_at).getTime()
	if (timeDiff > 5 * 60 * 1000) return true

	return false
}

export function ChatWindow({ conversationId, onBack, onConversationDeleted, visitorName: initialVisitorName, visitorEmail: initialVisitorEmail }: ChatWindowProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { toast } = useToast()
	const [newMessage, setNewMessage] = useState('')
	const [messages, setMessages] = useState<ChatMessage[]>([])
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [uploadPreview, setUploadPreview] = useState<string | null>(null)
	const [isUploading, setIsUploading] = useState(false)
	const [historyLimited, setHistoryLimited] = useState<boolean>(false)
	const [historyCutoff, setHistoryCutoff] = useState<string | null>(null)
	const [showEndDialog, setShowEndDialog] = useState(false)
	const [showDeleteDialog, setShowDeleteDialog] = useState(false)

	// Visitor context sidebar state - default to true (open)
	const [showVisitorInfo, setShowVisitorInfo] = useState(() => {
		const saved = localStorage.getItem('visitor-context-sidebar-open')
		return saved !== null ? saved === 'true' : true
	})

	const fileInputRef = useRef<HTMLInputElement>(null)
	const chatScrollRef = useRef<HTMLDivElement>(null)
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	// Persist sidebar state to localStorage
	useEffect(() => {
		localStorage.setItem('visitor-context-sidebar-open', String(showVisitorInfo))
	}, [showVisitorInfo])

	// Initialize read tracking hook
	const { observeMessage, markAllMessagesAsRead } = useMessageReadTracking({
		conversationId,
		messages,
		enabled: !!conversationId
	})

	const { data: conversation, isLoading: isLoadingConversation } = useQuery<ChatConversation, Error>({
		queryKey: ['conversation', conversationId],
		queryFn: () => apiClient(`/api/chat/conversations/${conversationId}`),
		enabled: !!conversationId,
	})

	// Fetch program agents for assignment dropdown
	const { data: programAgents = [], isLoading: isLoadingAgents } = useProgramAgents(conversation?.program_id)

	// Get plan info to check if team has pro access
	const { data: planInfo } = usePlanInfo()
	const isPro = planInfo?.isPremium || false

	const { data: messagesPayload, isLoading } = useQuery<{ messages: ChatMessage[], history_limited: boolean, history_cutoff: string | null }, Error>({
		queryKey: ['messages', conversationId],
		queryFn: async () => {
			return apiClient(`/api/chat/conversations/${conversationId}/messages`)
		},
		enabled: !!conversationId,
	})

	// Set messages when messagesPayload changes
	useEffect(() => {
		if (messagesPayload) {
			setMessages(messagesPayload.messages || [])
			setHistoryLimited(!!messagesPayload.history_limited)
			setHistoryCutoff(messagesPayload.history_cutoff || null)
		}
	}, [messagesPayload])

	// DEBUG: Log conversation and plan data
	useEffect(() => {
		if (conversation) {
			console.log('[ChatWindow] Conversation data:', {
				id: conversation.id,
				hasVisitorMetadata: !!conversation.visitor_metadata,
				visitorMetadataKeys: conversation.visitor_metadata ? Object.keys(conversation.visitor_metadata) : [],
				visitorMetadata: conversation.visitor_metadata
			})
		}
	}, [conversation])

	useEffect(() => {
		console.log('[ChatWindow] Plan info:', {
			isPro,
			planInfo
		})
	}, [isPro, planInfo])

	const endConversationMutation = useMutation({
		mutationFn: async () => {
			return apiClient(`/api/chat/conversations/${conversationId}/end`, {
				method: 'PATCH',
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['conversations'] })
			queryClient.invalidateQueries({
				queryKey: ['conversation', conversationId],
			})
			toast({
				title: t('chatWindow.conversationEnded'),
				description: t('chatWindow.conversationMarkedClosed'),
			})
		},
		onError: () => {
			toast({
				title: t('chatWindow.error'),
				description: t('chatWindow.failedToEndConversation'),
				variant: 'destructive',
			})
		},
	})

	const deleteConversationMutation = useMutation({
		mutationFn: async () => {
			return apiClient(`/api/chat/conversations/${conversationId}`, {
				method: 'DELETE',
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['conversations'] })
			toast({
				title: t('chatWindow.conversationDeleted'),
				description: t('chatWindow.conversationPermanentlyDeleted'),
			})
			if (onConversationDeleted) {
				onConversationDeleted()
			}
		},
		onError: () => {
			toast({
				title: t('chatWindow.error'),
				description: t('chatWindow.failedToDeleteConversation'),
				variant: 'destructive',
			})
		},
	})

	const sendMessageMutation = useMutation({
		mutationFn: async (content: string) => {
			const tempId = `temp-${Date.now()}`
			const optimisticMessage: ChatMessage = {
				id: tempId,
				conversation_id: conversationId,
				sender_type: 'agent',
				agent_id: user?.id || '',
				content,
				media_url: null,
				created_at: new Date().toISOString(),
			}

			setMessages((prev) => [...prev, optimisticMessage])

			const response = await apiClient(
				`/api/chat/conversations/${conversationId}/messages`,
				{
					method: 'POST',
					data: { content },
				}
			)

			return { response, tempId }
		},
		onSuccess: ({ response, tempId }) => {
			setMessages((prev) => {
				const filtered = prev.filter((msg) => msg.id !== tempId)
				if (response?.message && !filtered.find(msg => msg.id === response.message.id)) {
					return [...filtered, response.message].sort((a, b) =>
						new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
					)
				}
				return filtered
			})

			setNewMessage('')
			queryClient.invalidateQueries({ queryKey: ['conversations'] })
		},
		onError: (error, content, context) => {
			if (context) {
				setMessages((prev) => prev.filter((msg) => !msg.id.startsWith('temp-')))
			}
			toast({
				title: t('chatWindow.error'),
				description: t('chatWindow.failedToSendMessage'),
				variant: 'destructive',
			})
		},
	})

	useEffect(() => {
		if (chatScrollRef.current) {
			chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight
		}
	}, [messages])

	useEffect(() => {
		if (!conversationId) return

		const existingChannels = supabase.getChannels()
		existingChannels.forEach(ch => {
			if (ch.topic === `widget_chat:${conversationId}`) {
				supabase.removeChannel(ch)
			}
		})

		const channel = supabase
			.channel(`widget_chat:${conversationId}`)
			.on(
				'postgres_changes',
				{
					event: 'UPDATE',
					schema: 'public',
					table: 'chat_conversations',
					filter: `id=eq.${conversationId}`,
				},
				(payload) => {
					queryClient.invalidateQueries({
						queryKey: ['conversation', conversationId],
					})
					queryClient.invalidateQueries({ queryKey: ['conversations'] })
				}
			)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'chat_messages',
					filter: `conversation_id=eq.${conversationId}`,
				},
				(payload) => {
					const newMessage = payload.new as ChatMessage
					if (!newMessage) return

					setMessages((prev) => {
						const exists = prev.some((msg) => msg.id === newMessage.id)
						if (!exists) {
							return [...prev, newMessage].sort((a, b) =>
								new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
							)
						}
						return prev
					})
				}
			)
			.on(
				'postgres_changes',
				{
					event: 'UPDATE',
					schema: 'public',
					table: 'chat_messages',
					filter: `conversation_id=eq.${conversationId}`,
				},
				(payload) => {
					const updatedMessage = payload.new as ChatMessage
					if (!updatedMessage) return

					setMessages((prev) => {
						return prev.map((msg) =>
							msg.id === updatedMessage.id ? updatedMessage : msg
						)
					})
				}
			)
			.on(
				'broadcast',
				{ event: 'new_message' },
				(payload) => {
					const newMessage = payload.payload?.message as ChatMessage

					if (!newMessage) return

					if (
						newMessage.conversation_id &&
						newMessage.conversation_id !== conversationId
					) {
						return
					}

					setMessages((prev) => {
						const filtered = prev.filter((msg) => !msg.id.startsWith('temp-'))
						const exists = filtered.some((msg) => msg.id === newMessage.id)

						if (!exists) {
							return [...filtered, newMessage]
						}
						return filtered
					})
				}
			)
			.subscribe()

		return () => {
			supabase.removeChannel(channel)
		}
	}, [conversationId, queryClient])

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (file) {
			if (file.size > 5 * 1024 * 1024) {
				toast({
					title: t('chatWindow.fileTooLarge'),
					description: t('chatWindow.fileSizeMustBeLessThan5MB'),
					variant: 'destructive',
				})
				return
			}
			if (!file.type.startsWith('image/')) {
				toast({
					title: t('chatWindow.invalidFileType'),
					description: t('chatWindow.onlyImageFilesAllowed'),
					variant: 'destructive',
				})
				return
			}
			setSelectedFile(file)
			const previewUrl = URL.createObjectURL(file)
			setUploadPreview(previewUrl)
		}
	}

	const handleRemoveFile = () => {
		setSelectedFile(null)
		if (uploadPreview) {
			URL.revokeObjectURL(uploadPreview)
			setUploadPreview(null)
		}
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
	}

	const handleSendMessage = async (e: React.FormEvent) => {
		e.preventDefault()

		if (selectedFile) {
			setIsUploading(true)
			try {
				const formData = new FormData()
				formData.append('media', selectedFile)
				if (newMessage.trim()) {
					formData.append('content', newMessage)
				}

				const response = await apiClient(
					`/api/chat/conversations/${conversationId}/media`,
					{
						method: 'POST',
						body: formData,
					}
				)

				setNewMessage('')
				handleRemoveFile()
				queryClient.invalidateQueries({ queryKey: ['conversations'] })
				queryClient.invalidateQueries({ queryKey: ['messages', conversationId] })
			} catch (error) {
				console.error('Failed to upload file:', error)
				toast({
					title: t('chatWindow.uploadFailed'),
					description: t('chatWindow.failedToUploadFile'),
					variant: 'destructive',
				})
			} finally {
				setIsUploading(false)
			}
		} else if (newMessage.trim() && !sendMessageMutation.isPending) {
			sendMessageMutation.mutate(newMessage)
		}
	}

	const handleTextareaResize = useCallback(() => {
		const textarea = textareaRef.current
		if (!textarea) return

		textarea.style.height = 'auto'

		const maxHeight = window.innerWidth < 768 ? 200 : 240
		const minHeight = window.innerWidth < 768 ? 56 : 72
		const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
		textarea.style.height = `${newHeight}px`
	}, [])

	useEffect(() => {
		handleTextareaResize()
	}, [newMessage, handleTextareaResize])

	useEffect(() => {
		return () => {
			if (uploadPreview) {
				URL.revokeObjectURL(uploadPreview)
			}
		}
	}, [uploadPreview])

	// Calculate unread count
	const unreadCount = messages.filter(
		msg => msg.sender_type === 'visitor' && !msg.read_by_agent_at
	).length

	return (
		<div className="chat-window-container flex h-full bg-background">
			{/* Main Chat Area */}
			<div className="flex-1 flex flex-col min-w-0">
				{/* COMPACT HEADER - Single Row with Better Hierarchy */}
				<header className="border-b bg-background">
					<div className="px-4 py-3">
						<div className="flex items-center gap-3">
							{/* Left: Back Button + Visitor Info */}
							{onBack && (
								<Button
									variant="ghost"
									size="icon"
									onClick={onBack}
									className="shrink-0 h-9 w-9 hover:bg-muted/80 active:bg-muted"
									aria-label={t('chatWindow.goBackToConversationList')}>
									<ArrowLeft className="h-4 w-4" />
								</Button>
							)}

							{/* Center: Name + Email + Meta Info */}
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2 mb-0.5">
									<h2 className="text-base font-semibold truncate text-foreground">
										{conversation?.visitor_name || initialVisitorName || t('chatWindow.visitor')}
									</h2>
									{unreadCount > 0 && (
										<Badge className="bg-blue-500 text-white font-semibold px-1.5 py-0 text-xs shrink-0">
											{unreadCount}
										</Badge>
									)}
								</div>

								<div className="flex items-center gap-2 text-xs">
									{(conversation?.visitor_email || initialVisitorEmail) && (
										<span className="text-muted-foreground truncate">
											{conversation?.visitor_email || initialVisitorEmail}
										</span>
									)}
									{conversation?.assigned_agent && (
										<>
											{conversation?.visitor_email && (
												<span className="text-muted-foreground">•</span>
											)}
											<div className="flex items-center gap-1.5 min-w-0">
												<AgentAvatar
													agent={conversation.assigned_agent}
													size="xs"
													showStatus={false}
												/>
												<span className="text-muted-foreground truncate">
													{conversation.assigned_agent.user?.name || conversation.assigned_agent.user?.email || conversation.assigned_agent.name || conversation.assigned_agent.email}
												</span>
											</div>
										</>
									)}
								</div>
							</div>

							{/* Right: Assignment Dropdown + Visitor Info Toggle + More Menu */}
							<div className="flex items-center gap-1 shrink-0">
								{conversation && (
									<AssignmentDropdown
										conversationId={conversationId}
										currentAgent={conversation.assigned_agent}
										teamMembers={programAgents}
										programId={conversation.program_id}
										variant="ghost"
										size="sm"
									/>
								)}

								<Button
									variant="ghost"
									size="icon"
									className="h-9 w-9 hover:bg-muted/80"
									onClick={() => setShowVisitorInfo(!showVisitorInfo)}
									aria-label={t('chatWindow.toggleVisitorInformation')}
									aria-expanded={showVisitorInfo}
								>
									<Info className={cn("h-4 w-4 transition-colors", showVisitorInfo && "text-primary")} />
								</Button>

								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-9 w-9 hover:bg-muted/80"
											aria-label={t('chatWindow.openConversationMenu')}>
											<MoreHorizontal className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="w-56">
										{unreadCount > 0 && (
											<>
												<DropdownMenuItem onClick={markAllMessagesAsRead} className="gap-2 py-2">
													<CheckCheck className="h-4 w-4 text-blue-500" />
													<span>{t('chatWindow.markAllAsRead')}</span>
												</DropdownMenuItem>
												<DropdownMenuSeparator />
											</>
										)}
										<DropdownMenuItem
											onClick={() => {
												if (conversation?.status === 'closed') {
													toast({
														title: t('chatWindow.alreadyClosed'),
														description: t('chatWindow.conversationAlreadyClosed'),
													})
													return
												}
												setShowEndDialog(true)
											}}
											disabled={conversation?.status === 'closed'}
											className={cn(
												'gap-2 py-2',
												conversation?.status === 'open'
													? 'text-orange-600 dark:text-orange-400'
													: 'opacity-50 cursor-not-allowed'
											)}>
											<XCircle className="h-4 w-4" />
											<span>{t('chatWindow.endConversation')}</span>
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											onClick={() => setShowDeleteDialog(true)}
											className="gap-2 py-2 text-destructive focus:text-destructive">
											<Trash2 className="h-4 w-4" />
											<span>{t('chatWindow.deleteConversation')}</span>
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					</div>

					{historyLimited && (
						<div className="px-3 py-1.5 text-xs text-amber-800 bg-amber-50 border-t border-amber-200 flex items-center justify-between">
							<span>
								{t('chatWindow.showingLastDays', { 
									days: (() => {
										if (!historyCutoff) return 3
										const ms = Date.now() - new Date(historyCutoff).getTime()
										return Math.max(1, Math.round(ms / 86400000))
									})()
								})}
							</span>
							<button
								onClick={() => navigate('/settings?tab=billing')}
								className="text-amber-700 hover:text-amber-900 underline font-medium cursor-pointer"
							>
								{t('chatWindow.upgradeForFullHistory')}
							</button>
						</div>
					)}
				</header>

				{/* MESSAGES AREA */}
				<div className="chat-scroll-area flex-1 overflow-y-auto" ref={chatScrollRef}>
					<div className="p-4 sm:p-6">
						{isLoading ? (
							<div className="space-y-6">
								{[...Array(3)].map((_, i) => (
									<div key={i} className="flex justify-start">
										<div className="max-w-[85%] sm:max-w-[70%] space-y-2">
											<div className="flex items-center gap-2">
												<Skeleton className="h-6 w-6 rounded-full" />
												<Skeleton className="h-4 w-24" />
											</div>
											<Skeleton className="h-20 w-72 rounded-lg" />
										</div>
									</div>
								))}
							</div>
						) : messages.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-12 px-4 max-w-md mx-auto text-center">
								<div className="relative mb-6">
									<div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-full flex items-center justify-center mb-4">
										<Send className="w-7 h-7 text-primary/60 transform -rotate-12" />
									</div>
									<div className="absolute inset-0 w-16 h-16 bg-primary/5 rounded-full animate-ping opacity-20"></div>
								</div>
								<h3 className="text-lg font-semibold text-foreground mb-2">
									{t('chatWindow.startTheConversation')}
								</h3>
								<p className="text-sm text-muted-foreground mb-6 leading-relaxed">
									{t('chatWindow.visitorWaiting', { name: conversation?.visitor_name })}
									<br />
									{t('chatWindow.sendFriendlyMessage')}
								</p>
								<div className="flex flex-col gap-2 w-full">
									<p className="text-xs text-muted-foreground/80 mb-2 font-medium">
										{t('chatWindow.quickReplies')}:
									</p>
									<div className="grid gap-2">
										{[
											t('chatWindow.quickReply1'),
											t('chatWindow.quickReply2'),
											t('chatWindow.quickReply3')
										].map((suggestion, index) => (
											<button
												key={index}
												onClick={() => setNewMessage(suggestion)}
												className="group text-left p-3 text-sm bg-muted/30 hover:bg-muted/50 rounded-lg border border-muted/40 hover:border-muted/60 transition-all duration-200 hover:shadow-sm"
											>
												<span className="text-foreground/90 group-hover:text-foreground">
													{suggestion}
												</span>
											</button>
										))}
									</div>
								</div>
							</div>
						) : (
							<div className="space-y-4">
								{messages.map((msg, index) => {
									const showSender = shouldShowSenderInfo(messages, index)
									const messageSpacing = showSender ? 'mt-6' : 'mt-1'

									return (
										<div key={msg.id} className={cn(index > 0 && messageSpacing)}>
											<Message
												message={msg}
												agentId={user?.id}
												onObserve={observeMessage}
												showSenderInfo={showSender}
												visitorName={conversation?.visitor_name || initialVisitorName}
											/>
										</div>
									)
								})}
								<div ref={messagesEndRef} />
							</div>
						)}
					</div>
				</div>

				{/* Closed conversation banner */}
				{conversation?.status === 'closed' && (
					<div className="border-t bg-orange-50 dark:bg-orange-950/20 p-4">
						<div className="flex items-center justify-between max-w-4xl mx-auto">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
									<XCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
								</div>
								<div>
									<p className="font-medium text-orange-900 dark:text-orange-100">
										{t('chatWindow.conversationHasBeenClosed')}
									</p>
									<p className="text-sm text-orange-700 dark:text-orange-300 mt-0.5">
										{t('chatWindow.noNewMessagesCanBeSent')}
									</p>
								</div>
							</div>
						</div>
					</div>
				)}

				<footer className="chat-footer flex-shrink-0 border-t bg-background/95 backdrop-blur-sm">
					{uploadPreview && (
						<div className="p-3 border-b bg-muted/20">
							<div className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border/50">
								<div className="relative group">
									<img
										src={uploadPreview}
										alt={t('chatWindow.uploadPreview')}
										className="h-16 w-16 object-cover rounded-lg border border-border/50"
									/>
									<div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-foreground truncate">
										{selectedFile?.name}
									</p>
									<p className="text-xs text-muted-foreground">
										{selectedFile && (selectedFile.size / 1024).toFixed(1)} KB
									</p>
								</div>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 text-muted-foreground hover:text-destructive"
									onClick={handleRemoveFile}
									disabled={isUploading}
									aria-label={t('chatWindow.removeFile')}>
									<X className="h-4 w-4" />
								</Button>
							</div>
						</div>
					)}

					<form onSubmit={handleSendMessage} className="p-3 sm:p-4">
						<div className="relative">
							<textarea
								ref={textareaRef}
								value={newMessage}
								onChange={(e) => {
									setNewMessage(e.target.value)
									handleTextareaResize()
								}}
								placeholder={
									conversation?.status === 'closed'
										? t('chatWindow.conversationIsClosed')
										: selectedFile
										? t('chatWindow.addCaptionOptional')
										: t('chatWindow.typeYourMessage')
								}
								className={cn(
									'w-full rounded-xl px-4 py-3 pr-24',
									'text-sm leading-relaxed resize-none',
									'min-h-[52px] max-h-[200px]',
									'border-2 transition-all duration-200',
									'bg-muted/30 border-border/40',
									'placeholder:text-muted-foreground/60',
									'focus:bg-background focus:border-primary/50',
									'focus:ring-4 focus:ring-primary/10',
									'focus:outline-none',
									conversation?.status === 'closed' && [
										'bg-muted/20 border-border/20',
										'cursor-not-allowed opacity-60'
									]
								)}
								disabled={isUploading || conversation?.status === 'closed'}
								onKeyDown={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault()
										handleSendMessage(e)
									}
									if (e.key === 'Escape') {
										e.currentTarget.blur()
									}
								}}
								aria-label={t('chatWindow.messageInput')}
								aria-describedby="message-hint"
							/>
							<span id="message-hint" className="sr-only">
								{t('chatWindow.pressEnterToSend')}
							</span>
							<div className="absolute right-2 bottom-2 flex items-center gap-1">
								<input
									ref={fileInputRef}
									type="file"
									accept="image/*"
									onChange={handleFileSelect}
									className="hidden"
									aria-label={t('chatWindow.selectFileToUpload')}
								/>

								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted/50"
									onClick={() => fileInputRef.current?.click()}
									disabled={isUploading || !!selectedFile || conversation?.status === 'closed'}
									aria-label={t('chatWindow.attachFile')}>
									<Paperclip className="h-4 w-4" />
								</Button>

								<Button
									type="submit"
									size="icon"
									className={cn(
										'h-9 w-9 transition-all duration-200',
										'shadow-md hover:shadow-lg',
										(newMessage.trim() || selectedFile)
											? 'opacity-100 scale-100'
											: 'opacity-60 scale-95 cursor-not-allowed'
									)}
									disabled={
										sendMessageMutation.isPending ||
										isUploading ||
										(!newMessage.trim() && !selectedFile) ||
										conversation?.status === 'closed'
									}
									aria-label={t('chatWindow.sendMessage')}>
									{isUploading ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Send className="h-4 w-4" />
									)}
								</Button>
							</div>
						</div>
						<p className="mt-2 text-xs text-muted-foreground/60 px-1">
							{t('chatWindow.pressEnterToSend')}
						</p>
					</form>
				</footer>
			</div>

			<VisitorContextSidebar
				conversation={conversation}
				isOpen={showVisitorInfo}
				onToggle={() => setShowVisitorInfo(!showVisitorInfo)}
				isPro={isPro}
				visitorName={conversation?.visitor_name || initialVisitorName}
				visitorEmail={conversation?.visitor_email || initialVisitorEmail}
				isLoading={isLoadingConversation}
			/>

			<AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
				<AlertDialogContent className="max-w-md">
					<AlertDialogHeader>
						<div className="flex items-center gap-3">
							<div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
								<XCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
							</div>
							<AlertDialogTitle>{t('chatWindow.endThisConversation')}</AlertDialogTitle>
						</div>
						<AlertDialogDescription className="pt-2">
							{t('chatWindow.endConversationDescription')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('chatWindow.cancel')}</AlertDialogCancel>
						<AlertDialogAction
							className="bg-orange-600 hover:bg-orange-700"
							onClick={() => {
								endConversationMutation.mutate()
								setShowEndDialog(false)
							}}
						>
							{t('chatWindow.endConversation')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent className="max-w-md">
					<AlertDialogHeader>
						<div className="flex items-center gap-3">
							<div className="p-2 bg-destructive/10 rounded-full">
								<Trash2 className="h-5 w-5 text-destructive" />
							</div>
							<AlertDialogTitle>{t('chatWindow.deleteThisConversation')}</AlertDialogTitle>
						</div>
						<AlertDialogDescription className="pt-2">
							{t('chatWindow.deleteConversationDescription')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('chatWindow.cancel')}</AlertDialogCancel>
						<AlertDialogAction
							className="bg-destructive hover:bg-destructive/90"
							onClick={() => {
								deleteConversationMutation.mutate()
								setShowDeleteDialog(false)
							}}
						>
							{t('chatWindow.deletePermanently')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
